import React, { FC } from "react";

// types
import { IPaymentPlan } from "../../services/subscriptionsFeature";

// UI
import { Card } from "@UI/Components/Card/Card";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Tag } from "@UI/Components/Tag/Tag";
import { Button } from "@UI/Components/Button/Button";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import useMediaQuery from "@UI/hooks/useMediaQuery";

// Core
import useCurrency from "@Libs/Utils/hooks/useCurrency";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { FormattedNumber } from "@I18n/Components/FormattedNumber/FormattedNumber";
import { FormattedPeriodOfTime } from "@I18n/Components/FormattedPeriodOfTime/FormattedPeriodOfTime";
import messages from "./messages";

// types
import { Bold } from "@UI/Components/Bold/Bold";
import useI18n from "@I18n/hooks/useI18n";
import { ISubscriptionPlan } from "@modules/Subscriptions/types";
import { useSubscriptionsFeature } from "@modules/Subscriptions/hooks/useSubscriptionsFeature";
import { isEmpty } from "lodash-es";

interface Props {
  subscriptionPlan: ISubscriptionPlan;
  onClick?: (subscriptionPlan: ISubscriptionPlan) => void;
}

export const SUBSCRIPTION_PLAN_CMS_NAMES: {
  [key in ISubscriptionPlan["name"]]: IPaymentPlan["name"];
} = {
  monthly_subscription: "Monthly",
  quarterly_subscription: "Quarterly",
  yearly_subscription: "Yearly",
};

export const SubscriptionPlanCard: FC<Props> = ({
  subscriptionPlan,
  onClick,
}) => {
  const { localize } = useI18n();
  const { currencySymbol } = useCurrency();
  const { isDesktop, isMobile } = useMediaQuery();
  const { getPlanContent, isLoading: isSubscriptionsFeatureLoading } =
    useSubscriptionsFeature();

  if (isSubscriptionsFeatureLoading) {
    return <LoadingScreen />;
  }

  const cmsContentForPlan = getPlanContent(subscriptionPlan);

  const badgeEnabled =
    cmsContentForPlan.badge.enabled &&
    !isEmpty(localize(cmsContentForPlan.badge.text, false));

  const handleClick = async (e) => {
    e.stopPropagation();
    try {
      await onClick?.(subscriptionPlan);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Card
      as={!isDesktop ? "a" : "div"}
      data-testid={`${cmsContentForPlan.name.toLowerCase()}-plan-card`}
      corner={
        cmsContentForPlan?.corner?.icon ? cmsContentForPlan.corner : false
      }
      paddingY={{ base: 3, lg: 6 }}
      paddingLeft={{ base: 4, lg: 6 }}
      paddingRight={{ base: 0, sm: 3, lg: 6 }}
      flexDirection={{ base: "row", lg: "column" }}
      alignItems={{ base: "center", lg: "stretch" }}
      textAlign={{ base: "start", lg: "center" }}
      minHeight="24"
      shadow={{ base: "md", lg: "lg" }}
      onClick={handleClick}
      header={
        <Flex
          alignItems={{ base: "start", lg: "center" }}
          flexDirection="column"
          paddingRight={{ base: 0, sm: 3, lg: 0 }}
          flexGrow={0}
        >
          <Heading
            as="h4"
            fontSize={{ base: "md", lg: "lg" }}
            marginBottom={badgeEnabled && "1"}
            fontFamily="body"
          >
            <FormattedMessage
              {...messages?.[`RT_${cmsContentForPlan.name.toLowerCase()}`]}
            />
          </Heading>
          {badgeEnabled ? (
            <Tag
              rounded="full"
              flexShrink={0}
              size={isMobile ? "sm" : "md"}
              colorScheme="seaGreen"
            >
              <FormattedMessage
                {...messages?.[`${cmsContentForPlan.name.toLowerCase()}_badge`]}
              />
            </Tag>
          ) : (
            isDesktop && <Box as="span" height="24px" />
          )}
        </Flex>
      }
      footer={
        isDesktop ? (
          <Button
            size="lg"
            width="full"
            colorScheme="seaGreen"
            data-testid="choose-plan"
            onClick={handleClick}
          >
            <FormattedMessage {...messages.chooseSubscriptionPlan} />
          </Button>
        ) : (
          <Button
            variant="link"
            data-testid="choose-plan"
            onClick={handleClick}
          >
            <Icon as={ArrowRight} color="gray.700" />
          </Button>
        )
      }
    >
      <Box
        display="flex"
        justifyContent="center"
        alignItems={{ base: "end", lg: "center" }}
        flexGrow={1}
        flexDirection="column"
        paddingBottom={{ lg: 8 }}
        paddingTop={{ lg: 4 }}
        paddingLeft={{ base: 4, lg: "inherit" }}
      >
        <Flex
          alignItems={{ base: "flex-end", lg: "center" }}
          paddingBottom={{ base: 1, lg: 2 }}
        >
          <Bold
            fontSize={{ base: "xl", lg: "5xl" }}
            lineHeight="1"
            marginTop={{ base: 1, lg: "inherit" }}
          >
            <FormattedNumber value={subscriptionPlan.priceCents / 100} />
          </Bold>
          <Flex paddingLeft="1" textAlign="start">
            <Bold
              fontSize={{ base: "xs", lg: "sm" }}
              lineHeight="1"
              display="block"
            >
              {currencySymbol("EUR")}
            </Bold>
            <Text
              fontSize={{ base: "xs", lg: "sm" }}
              fontWeight="light"
              color="gray.700"
              lineHeight="1"
              whiteSpace="nowrap"
            >
              {" / "}
              <FormattedPeriodOfTime
                value={cmsContentForPlan.period}
                unit={cmsContentForPlan.periodUnit}
              />
            </Text>
          </Flex>
        </Flex>
        {cmsContentForPlan.description.enabled && (
          <Text as="span" fontSize={{ base: "xs", lg: "md" }} flexShrink={0}>
            <FormattedMessage
              {...messages?.[
                `${cmsContentForPlan.name.toLowerCase()}_payment_description`
              ]}
              values={{
                currency: currencySymbol("EUR"),
              }}
            />
          </Text>
        )}
      </Box>
    </Card>
  );
};
